<template>
    <div :class="cardClass" @click="isSelectionAllowed && selectionChanged(physician.id)">
        <div v-if="isSelectionAllowed && checked" class="card__selected-check">
            <common-icon name="white-check" size="small"></common-icon>
        </div>

        <common-context-menu
            v-if="showContextMenu"
            class="card__context-menu"
            :items="contextMenuItems"
            @modify-privileges="modifyPrivileges"
            @remove-member="openRemovePracticeMemberModal"
        ></common-context-menu>

        <div v-if="showCardFavorite" class="card__favorite">
            <card-favorite :user="physician"></card-favorite>
        </div>

        <!-- Grid View -->
        <template v-if="viewType === 'grid'">
            <div class="card-header">
                <div class="info">
                    <div class="name-row">
                        <common-rating-avatar
                            :rating="physician.professionalRating"
                            :user="physician"
                            :size="avatarSize"
                            :isUserProfileLinkEnabled="isUserProfileLinkEnabled"
                            :showPracticeAvatar="shouldShowPracticeAvatar"
                        ></common-rating-avatar>

                        <div class="tags">
                            <div class="main-name-row">
                                <h3>
                                    <common-network-user-name :user="physician"></common-network-user-name>
                                </h3>
                                <span v-if="physician?.isNpiVerified" class="verified">
                                    <common-icon name="verify" size="small" class="verify-icon"></common-icon>
                                    {{ $t('pages.network.connections.network_profile_detail.verify') }}
                                </span>
                                <span v-if="physician?.rating" class="rating">
                                    <common-icon name="star" size="small"></common-icon>
                                    <span class="rating-count">{{ physician?.rating || 0 }}</span>
                                    <span class="check"><common-icon name="check" size="small"></common-icon></span>
                                </span>
                            </div>

                            <p class="clinic">
                                {{
                                    showMasterUserAuthorities
                                        ? $get(physician, 'userRole.name')
                                        : $get(physician, 'practice.name') || physician.name
                                }}
                            </p>
                            <span v-if="isBoardCertified" class="tag">
                                <span class="check"><common-icon name="check" size="small"></common-icon></span>
                                {{ $t('pages.network.connections.network_profile_detail.board') }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card-body">
                <p v-if="physician?.specialities && physician.specialities.length" class="speciality">
                    {{ physician.specialities[0].speciality.name }}
                </p>
                <p v-else-if="physician?.facilityServiceTypes && physician.facilityServiceTypes.length" class="speciality">
                    {{ physician.facilityServiceTypes[0].name }}
                </p>

                <p v-if="locationCity" class="location">
                    <common-icon name="location" size="small"></common-icon>
                    {{ locationCity }}, {{ locationState }}
                    <span v-if="(!showPhoneButton || isPhoneVisible) && physicianPhone" class="inline-phone">
                        •
                        <common-icon name="phones" size="small"></common-icon>
                        {{ physicianPhone }}
                    </span>
                    <span v-else-if="showPhoneButton && physicianPhone" class="inline-phone inline-phone--desktop">
                        •
                        <common-icon name="phones" size="small"></common-icon>
                        {{ physicianPhone }}
                    </span>
                </p>

                <div v-if="hasPracticeChoice" class="practice-choice" @click.stop>
                    <p class="practice-choice__title">
                        <common-icon name="location" size="small"></common-icon>
                        {{ $t('elements.referrals.provider_search_card.practice_choice_title') }}
                    </p>
                    <p class="practice-choice__hint">
                        {{
                            $t('elements.referrals.provider_search_card.practice_choice_hint', {
                                count: practiceChoiceOptions.length,
                            })
                        }}
                    </p>
                    <select-input
                        class="practice-choice__select"
                        :options="practiceChoiceOptions"
                        :value="chosenPracticeUserId"
                        @change="choosePractice"
                    ></select-input>
                </div>

                <p v-if="showPending || physician.isInvitee" class="connections card__connection-pending">
                    {{ $t('elements.referrals.card.pending') }}
                </p>
                <p v-else-if="physician?.connectionCount" class="connections">
                    {{ physician.connectionCount }} {{ $t('elements.referrals.card.connections') }} • in my network
                </p>

                <network-inlusion-status
                    v-if="showNetworkStatus"
                    :isInMyNetwork="isInMyNetwork"
                    :targetType="targetType"
                    :targetId="Number(targetId)"
                    :provider="physician"
                    @added-to-network="emitEvent({ name: 'added-to-network' })"
                ></network-inlusion-status>
            </div>

            <div v-if="isStandalone" class="card-footer">
                <common-button
                    v-if="showPending || physician.isInvitee"
                    icon="send"
                    class="refer-btn reinvite-btn"
                    :label="$t('common.microcopy.reinvite')"
                    size="large"
                    type="primary"
                    :isDisabled="disableReferButton"
                    @click.stop="toggleResendInvitationModal()"
                ></common-button>
                <common-button
                    v-else-if="shouldShowReferButton"
                    icon="refer"
                    class="refer-btn"
                    :label="$t('pages.network.connections.buttons.refer')"
                    size="large"
                    type="primary"
                    :isDisabled="disableReferButton"
                    @click.stop="itemClicked()"
                ></common-button>
                <div class="actions">
                    <common-button
                        v-if="showEmailButton && physician?.email"
                        icon="chat"
                        class="icon-btn"
                        size="small"
                        type="info"
                        @click.stop="toggleshowEmailForm()"
                    ></common-button>
                    <common-button
                        v-if="showPhoneButton && physicianPhone"
                        icon="phones"
                        class="icon-btn icon-btn--phone-mobile"
                        size="small"
                        type="info"
                        @click.stop="handlePhoneClick"
                    ></common-button>
                </div>
            </div>
        </template>

        <!-- List View -->
        <template v-else>
            <div class="card-header">
                <div class="info">
                    <div class="name-row">
                        <common-rating-avatar
                            :rating="physician.professionalRating"
                            :user="physician"
                            :size="avatarSize"
                            :isUserProfileLinkEnabled="isUserProfileLinkEnabled"
                            :showPracticeAvatar="shouldShowPracticeAvatar"
                        ></common-rating-avatar>

                        <div class="tags">
                            <div class="name-row">
                                <h3>
                                    <common-network-user-name :user="physician"></common-network-user-name>
                                </h3>
                                <span v-if="physician?.isNpiVerified" class="verified">
                                    <common-icon name="verify" size="small" class="verify-icon"></common-icon>
                                    {{ $t('pages.network.connections.network_profile_detail.verify') }}
                                </span>
                                <span v-if="isBoardCertified" class="tag">
                                    <span class="check"><common-icon name="check" size="small"></common-icon></span>
                                    {{ $t('pages.network.connections.network_profile_detail.board') }}
                                </span>
                                <span v-if="showPending || physician.isInvitee" class="connections card__connection-pending">
                                    {{ $t('elements.referrals.card.pending') }}
                                </span>
                            </div>
                            <div class="detail">
                                <p class="clinic">
                                    {{
                                        showMasterUserAuthorities
                                            ? $get(physician, 'userRole.name')
                                            : $get(physician, 'practice.name') || physician.name
                                    }}
                                </p>
                                <p v-if="physician?.specialities && physician.specialities.length" class="speciality">
                                    {{ physician.specialities[0].speciality.name }}
                                </p>
                                <p
                                    v-else-if="physician?.facilityServiceTypes && physician.facilityServiceTypes.length"
                                    class="speciality"
                                >
                                    {{ physician.facilityServiceTypes[0].name }}
                                </p>

                                <div class="name-row">
                                    <p v-if="locationCity" class="location">
                                        <common-icon name="location" size="small"></common-icon>
                                        {{ locationCity }}, {{ locationState }}
                                        <span v-if="physician?.connectionCount">•</span>
                                    </p>
                                    <p v-if="physician?.connectionCount" class="connections">
                                        {{ physician.connectionCount }} {{ $t('elements.referrals.card.connections') }}
                                    </p>
                                </div>
                                <div class="name-row" style="margin-top: 4px">
                                    <p
                                        v-if="(!showPhoneButton || isPhoneVisible) && physicianPhone"
                                        class="location inline-phone-row"
                                    >
                                        <common-icon name="phones" size="small"></common-icon>
                                        {{ physicianPhone }}
                                    </p>
                                    <p
                                        v-else-if="showPhoneButton && physicianPhone"
                                        class="location inline-phone-row inline-phone-row--desktop"
                                    >
                                        <common-icon name="phones" size="small"></common-icon>
                                        {{ physicianPhone }}
                                    </p>
                                </div>

                                <div v-if="hasPracticeChoice" class="practice-choice" @click.stop>
                                    <p class="practice-choice__title">
                                        <common-icon name="location" size="small"></common-icon>
                                        {{ $t('elements.referrals.provider_search_card.practice_choice_title') }}
                                    </p>
                                    <p class="practice-choice__hint">
                                        {{
                                            $t('elements.referrals.provider_search_card.practice_choice_hint', {
                                                count: practiceChoiceOptions.length,
                                            })
                                        }}
                                    </p>
                                    <select-input
                                        class="practice-choice__select"
                                        :options="practiceChoiceOptions"
                                        :value="chosenPracticeUserId"
                                        @change="choosePractice"
                                    ></select-input>
                                </div>
                            </div>
                        </div>
                    </div>

                    <network-inlusion-status
                        v-if="showNetworkStatus"
                        :isInMyNetwork="isInMyNetwork"
                        :targetType="targetType"
                        :targetId="Number(targetId)"
                        :provider="physician"
                        @added-to-network="emitEvent({ name: 'added-to-network' })"
                    ></network-inlusion-status>

                    <div class="card-footer">
                        <span class="rating">
                            <div v-if="physician?.rating">
                                <common-icon name="star" size="small"></common-icon>
                                <span class="rating-count">{{ physician?.rating || 0 }}</span>
                                <span class="check"><common-icon name="check" size="small"></common-icon></span>
                            </div>
                        </span>
                        <div class="actions">
                            <common-button
                                v-if="showPending || physician.isInvitee"
                                icon="send"
                                class="refer-btn reinvite-btn"
                                :label="$t('common.microcopy.reinvite')"
                                size="small"
                                type="primary"
                                :isDisabled="disableReferButton"
                                @click.stop="toggleResendInvitationModal()"
                            ></common-button>
                            <common-button
                                v-else-if="shouldShowReferButton"
                                icon="refer"
                                class="refer-btn"
                                :label="$t('pages.network.connections.buttons.refer')"
                                size="small"
                                type="primary"
                                :isDisabled="disableReferButton"
                                @click.stop="itemClicked()"
                            ></common-button>
                            <common-button
                                v-if="showEmailButton && physician?.email"
                                icon="chat"
                                class="icon-btn"
                                size="small"
                                type="info"
                                @click.stop="toggleshowEmailForm()"
                            ></common-button>
                            <common-button
                                v-if="showPhoneButton && physicianPhone"
                                icon="phones"
                                class="icon-btn icon-btn--phone-mobile"
                                size="small"
                                type="info"
                                @click.stop="handlePhoneClick"
                            ></common-button>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <direct-referral
            v-if="isDirectReferralModalOpen"
            :activePracticeType="practiceType"
            :user="physician"
            :showAsInnerModal="showDirectReferralAsInnerModal"
            @location-selected="toggleDirectReferralModal"
            @close="toggleDirectReferralModal"
        ></direct-referral>
        <common-modal v-if="showEmailForm" @close="closeSendNetworkMessageModal()">
            <email-form
                headerText="Send email"
                :defaultUser="physician"
                :showBccWarning="true"
                :showOpenSelectMemberModalButton="true"
                @email-data="sendEmail"
                @close="toggleshowEmailForm()"
            ></email-form>
        </common-modal>

        <practice-privileges-modal
            v-if="showMasterUserAuthorities"
            :isOpen="isPracticePrivilegesModalOpen"
            :user="physician"
            :isModifyingPrivileges="isModifyingPrivileges"
            @close="closePracticePrivilegesModal"
            @membership-approved="approvePracticeMembership"
            @modify-privileges="modifyUserPrivileges"
        ></practice-privileges-modal>
        <remove-practice-member-modal
            v-if="showMasterUserAuthorities"
            :isOpen="isRemovePracticeMemberModalOpen"
            :user="physician"
            @close="closeRemovePracticeMemberModal"
            @member-removed="practiceMemberRemoved"
        ></remove-practice-member-modal>
        <resend-invitation-modal
            v-if="showMasterUserAuthorities"
            :isOpen="isResendInvitationModalOpen"
            :user="physician"
            @close="toggleResendInvitationModal()"
            @invitation-resent="invitationResent"
        ></resend-invitation-modal>
        <common-notification ref="commonSuccessNotification" :content="notificationMessage"></common-notification>
    </div>
</template>

<script>
    import { importComponents } from '~/helpers'
    import DirectReferral from 'components/page-elements/referrals/DirectReferral'
    import EmailForm from 'components/forms/connections/EmailForm'
    import PracticePrivilegesModal from 'components/page-elements/practice/PracticePrivilegesModal'
    import RemovePracticeMemberModal from 'components/page-elements/practice/RemovePracticeMemberModal'
    import ResendInvitationModal from 'components/page-elements/practice/ResendInvitationModal'
    import NetworkInlusionStatus from 'components/page-elements/network/NetworkInlusionStatus'
    import SelectInput from 'components/form-inputs/SelectInput'
    import PhysicianMixin from 'mixins/PhysicianMixin'
    import UserMembershipStatusEnum from '../../../enums/UserMembershipStatusEnum'
    import UserRolesEnum from '@/enums/UserRolesEnum'
    import UsersAPI from 'api/UsersAPI'
    import UserTypeEnum from '@/enums/UserTypeEnum'
    import PracticePlanEnum from '@/enums/PracticePlanEnum'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import { openEmailClient } from '~/utils'

    export default {
        mixins: [PhysicianMixin],

        components: {
            ...importComponents(require.context('components/page-elements/referrals-new-card')),
            DirectReferral,
            EmailForm,
            PracticePrivilegesModal,
            RemovePracticeMemberModal,
            ResendInvitationModal,
            NetworkInlusionStatus,
            SelectInput,
        },

        props: {
            isStandalone: { type: Boolean, default: true },
            showNetworkInclusionStatus: { type: Boolean, default: true },
            showMasterUserAuthorities: { type: Boolean, default: false },
            showInvitedDate: { type: Boolean, default: false },
            showNewUserWarning: { type: Boolean, default: false },
            showReferralsCount: { type: Boolean, default: true },
            showFavorite: { type: Boolean, default: true },
            isFixedHeight: { type: Boolean, default: true },
            showDirectReferralAsInnerModal: { type: Boolean, default: false },
            showCardAvailability: { type: Boolean, default: true },
            isUserProfileLinkEnabled: { type: Boolean, default: true },
            isSelectionAllowed: { type: Boolean, default: false },
            checkedValues: { type: Array, default: () => [] },
            viewType: { type: String, default: 'grid' },
            disableReferButton: { type: Boolean, default: false },
            showEmailButton: { type: Boolean, default: true },
            showPhoneInline: { type: Boolean, default: false },
            showPhoneButton: { type: Boolean, default: true },
            useExternalReferHandler: { type: Boolean, default: false },
            showReferButton: { type: Boolean, default: true },
            // A provider on several MedMatch practices: the ones that fit the search, best first
            // (GET referrals/npi-practices), and the chosen account's userId. A picker shows when there are 2+.
            practices: { type: Array, default: () => [] },
            chosenPracticeUserId: { type: Number, default: null },
        },

        data() {
            return {
                isProcessingApproval: false,
                isPracticePrivilegesModalOpen: false,
                isRemovePracticeMemberModalOpen: false,
                isResendInvitationModalOpen: false,
                isModifyingPrivileges: false,
                isDirectReferralModalOpen: false,
                showEmailForm: false,
                isPhoneVisible: false,
                practiceType: PracticeTypeEnum.PHYSICIAN,
                PracticePlanEnum: PracticePlanEnum,
                notificationMessage: '',
                contextMenuItems: [
                    {
                        label: this.$t('elements.referrals.card.context_menu.modify_privileges'),
                        name: 'modify-privileges',
                    },
                    {
                        label: this.$t('elements.referrals.card.context_menu.remove_member'),
                        name: 'remove-member',
                    },
                ],
            }
        },

        computed: {
            hasPracticeChoice() {
                return this.practiceChoiceOptions.length > 1
            },

            practiceChoiceOptions() {
                return this.practices.map((practice) => {
                    const place = [practice.city, practice.state].filter(Boolean).join(', ')
                    const distance =
                        practice.distanceMiles != null
                            ? this.$t('elements.referrals.provider_search_card.practice_distance', {
                                  miles: practice.distanceMiles,
                              })
                            : ''
                    return {
                        value: practice.userId,
                        label: [practice.practiceName, place, distance].filter(Boolean).join(' · '),
                    }
                })
            },

            isPhysicianRole() {
                const userRole = this.physician?.userRole
                const roleId = userRole?.id

                if (!userRole || roleId === 0) {
                    return true
                }

                return [
                    UserRolesEnum.PHYSICIAN.ID,
                    UserRolesEnum.NURSE_PRACTITIONER.ID,
                    UserRolesEnum.TEMPORARY_PHYSICIAN.ID,
                ].includes(roleId)
            },

            isBoardCertified() {
                return this.isPhysicianRole && Boolean(this.physician?.boardCertified)
            },

            shouldShowReferButton() {
                return this.showReferButton && this.isPhysicianRole
            },

            avatarSize() {
                return 'member-switch'
            },

            shouldShowPracticeAvatar() {
                if (this.isPhysician) return false
                if (this.physician?.avatar || this.physician?.profilePicture || this.physician?.firstName) return false
                return true
            },

            physicianPhone() {
                return this.physician?.mobilePhone || this.physician?.phone || null
            },

            locationCity() {
                return (
                    this.physician?.practice?.locations?.[0]?.city ||
                    this.physician?.locations?.[0]?.city ||
                    this.physician?.practice?.city ||
                    null
                )
            },

            locationState() {
                return (
                    this.physician?.practice?.locations?.[0]?.state ||
                    this.physician?.locations?.[0]?.state ||
                    this.physician?.practice?.state ||
                    null
                )
            },

            showNetworkStatus() {
                return this.isActiveUser && this.showNetworkInclusionStatus
            },
            showCardFavorite() {
                return this.isActiveUser && !this.isStandalone && this.showFavorite
            },

            isActiveUser() {
                return this.$auth.getActiveMember()
            },
            cardClass() {
                const isGrid = this.viewType === 'grid'
                return {
                    card: isGrid,
                    'card-layout': !isGrid,
                    'card__fixed-hight': this.isFixedHeight && isGrid,
                    'card--is-standalone': this.isStandalone && isGrid,
                    'card--is-selected': this.checked && isGrid,
                    'card--is-selectable': this.isSelectionAllowed && isGrid,
                    'card-layout--is-selected': this.checked && !isGrid,
                    'card-layout--is-selectable': this.isSelectionAllowed && !isGrid,
                }
            },

            targetType() {
                return this.isPhysician ? UserTypeEnum.USER : UserTypeEnum.PRACTICE
            },

            targetId() {
                return this.physician?.id
            },

            isPendingApproval() {
                return (
                    this.showMasterUserAuthorities &&
                    this.$auth.getUser().isMasterUser &&
                    this.physician.practiceMembershipStatus === UserMembershipStatusEnum.PENDING
                )
            },
            isPracticeNetwork() {
                return this.physician?.practice?.subscription === PracticePlanEnum.NETWORK
            },
            isEligibleForResendingInvitation() {
                return this.showMasterUserAuthorities && this.$auth.getUser().isMasterUser && this.physician.isInvitee
            },

            isMembershipApproved() {
                return this.physician.practiceMembershipStatus === UserMembershipStatusEnum.APPROVED
            },

            showContextMenu() {
                return (
                    this.showMasterUserAuthorities &&
                    this.isMembershipApproved &&
                    this.$auth.getUser().id !== this.physician?.id &&
                    this.$auth.getActiveMember().id !== this.physician?.id
                )
            },

            userHasPendingInvitation() {
                return this.physician.target && !this.physician.target.dateJoined
            },

            showPending() {
                return this.isPendingApproval || this.userHasPendingInvitation
            },

            isInMyNetwork() {
                return this.physician.isInMyNetwork !== undefined ? this.physician.isInMyNetwork : true
            },

            availabilityProps() {
                if (this.showMasterUserAuthorities) {
                    return this.ctaMasterUserProps
                }
                return this.showDirectReferralAsInnerModal ? this.ctaInnerModalProps : this.ctaProps
            },

            checked() {
                return this.checkedValues.includes(this.physician.id)
            },
        },

        methods: {
            choosePractice(userId) {
                this.emitEvent({ name: 'choose-practice', payload: Number(userId) })
            },

            updatePracticeMembershipStatus(action, data) {
                this.isProcessingApproval = true
                UsersAPI.updatePracticeMembershipStatus({ userId: this.physician?.id, action, data }).then(() => {
                    this.isProcessingApproval = false
                    this.emitEvent({ name: 'membership-status-change', payload: action })
                })
            },

            closePracticePrivilegesModal() {
                this.isModifyingPrivileges = false
                this.isPracticePrivilegesModalOpen = false
            },

            openPracticePrivilegesModal() {
                this.isPracticePrivilegesModalOpen = true
            },

            closeRemovePracticeMemberModal() {
                this.isRemovePracticeMemberModalOpen = false
            },

            toggleResendInvitationModal() {
                this.isResendInvitationModalOpen = !this.isResendInvitationModalOpen
            },

            openRemovePracticeMemberModal() {
                this.isRemovePracticeMemberModalOpen = true
            },

            approvePracticeMembership(data) {
                this.updatePracticeMembershipStatus('approve', data)
                this.closePracticePrivilegesModal()
            },

            modifyUserPrivileges(data) {
                UsersAPI.modifyUserPrivileges({ userId: this.physician?.id, data }).then(() => {
                    this.isProcessingApproval = false
                })
                this.emitEvent({
                    name: 'privileges-updated',
                    payload: { userId: this.physician?.id, ...data },
                })
                this.closePracticePrivilegesModal()
            },

            modifyPrivileges() {
                this.isModifyingPrivileges = true
                this.openPracticePrivilegesModal()
            },

            practiceMemberRemoved(payload) {
                this.closeRemovePracticeMemberModal()
                this.emitEvent({ name: 'member-removed', payload })
            },

            invitationResent(payload) {
                this.toggleResendInvitationModal()
                this.emitEvent({ name: 'invitation-resent', payload })
            },

            selectionChanged(id) {
                this.emitEvent({ name: 'selection-changed', payload: id })
            },

            inviteUserToSubscribe(id) {
                UsersAPI.inivteUserToSubscribe(id).then(() => {
                    this.notificationMessage = this.$t('modals.make_direct_referal_modal.inivte_billing.invite_success')
                    this.$refs.commonSuccessNotification.showNotification()
                })
            },

            itemClicked() {
                if (this.useExternalReferHandler) {
                    this.$emit('refer', this.physician)
                } else {
                    this.toggleDirectReferralModal()
                }
            },

            toggleDirectReferralModal() {
                this.isDirectReferralModalOpen = !this.isDirectReferralModalOpen
            },

            toggleshowEmailForm() {
                this.showEmailForm = !this.showEmailForm
            },

            closeSendNetworkMessageModal() {
                this.showEmailForm = false
            },

            sendEmail(event) {
                openEmailClient({
                    to: event.to,
                    cc: event.cc,
                    bcc: event.bcc,
                    subject: event.subject,
                    message: event.message,
                })
            },

            handlePhoneClick() {
                if (this.showPhoneInline) {
                    this.isPhoneVisible = !this.isPhoneVisible
                } else {
                    this.makeCall()
                }
            },

            makeCall() {
                if (!this.physicianPhone) return
                window.location.href = `tel:${this.physicianPhone}`
            },
        },
    }
</script>
<style scoped lang="scss">
    @import 'styles/components/network/_network';

    .card__selected-check {
        position: absolute;
        top: 14px;
        right: 14px;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background-color: #13b3e3;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10;
        box-shadow: 0 2px 5px rgba(19, 179, 227, 0.35);

        ::v-deep .icon,
        .icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 14px;
            height: 14px;
        }
    }

    .inline-phone,
    .inline-phone-row {
        color: inherit;
        white-space: nowrap;
        margin-left: 2px;
        display: inline-flex;
        align-items: center;
        gap: 6px;

        &::v-deep .icon {
            width: 14px;
            height: 14px;
            font-size: 14px;
        }
    }

    .icon-btn--phone-mobile {
        display: none;
    }

    .inline-phone--desktop,
    .inline-phone-row--desktop {
        display: inline-flex;
    }

    .card--is-standalone {
        padding: 25px;

        @media (max-width: 767px) {
            padding: 16px;
        }
    }

    .card:not(.card--is-standalone) {
        border: none;
        box-shadow: none;
        padding: 0;
    }

    .name-row h3 {
        font-size: 16px;
        font-weight: 700;

        @media (max-width: 480px) {
            font-size: 14px;
        }
    }

    .card__connection-pending {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        color: #ff6d00;
        font-weight: 600;
        font-size: 14px;
        padding-left: 0;
        position: relative;
        width: 100%;

        &::before {
            content: '';
            position: static;
            transform: none;
            display: inline-block;
            height: 10px;
            width: 10px;
            min-width: 10px;
            border-radius: 50%;
            background: #ff6d00;
        }
    }

    .reinvite-btn::v-deep .button {
        background-color: #13b3e3 !important;
        border-color: #13b3e3 !important;
    }

    // A provider on several practices: a highlighted panel so the referrer sees they must pick one.
    // Rendered only when there is a choice, so other cards keep no empty space for it.
    .practice-choice {
        display: flex;
        flex-direction: column;
        gap: 4px;
        margin-top: 12px;
        padding: 10px 12px 12px;
        border: 1px solid rgba(19, 179, 227, 0.45);
        border-left: 4px solid #13b3e3;
        border-radius: 10px;
        background: rgba(19, 179, 227, 0.08);
        cursor: default;

        &__title {
            display: flex;
            align-items: center;
            gap: 6px;
            margin: 0;
            font-size: 13px;
            font-weight: 700;
            color: #0b7fa3;

            ::v-deep .icon {
                width: 14px;
                height: 14px;
            }
        }

        &__hint {
            margin: 0 0 4px;
            font-size: 12px;
            color: #4b5563;
        }

        &__select {
            width: 100%;

            ::v-deep .form-input-select {
                background: #fff;
            }
        }

        // The chosen practice stays on one line inside the field; the open list shows each option in full.
        ::v-deep .form-input-select__icon-label {
            flex: 1;
            min-width: 0;
        }

        ::v-deep .form-input-select__label {
            display: block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        // Practice, city and distance stay readable on a phone: options wrap instead of being cut off.
        ::v-deep .form-input-select__option {
            height: auto;
            min-height: 40px;
            padding-top: 8px;
            padding-bottom: 8px;
            text-align: left;

            span {
                white-space: normal;
                overflow: visible;
            }
        }
    }

    .card-body .network-inclusion-status {
        margin-top: 12px;
        display: flex;
        justify-content: center;
        padding: 0 16px;
    }

    .detail-status {
        margin-top: 2px;
    }

    @media (max-width: 767px) {
        .icon-btn--phone-mobile {
            display: inline-flex;
        }

        .inline-phone--desktop,
        .inline-phone-row--desktop {
            display: none;
        }

        .card-layout .card-header .info {
            flex-direction: column;
            gap: 12px;
        }

        .card-layout .card-header .info > .name-row {
            flex-wrap: wrap;
        }

        .card-layout .card-header .info > .name-row > .tags > .detail > .name-row {
            flex-wrap: wrap;
            gap: 4px;
        }

        .card-layout .card-footer {
            margin-top: 12px;
            justify-content: flex-start;
            flex-wrap: nowrap;
            gap: 6px;
        }

        .card-layout .card-footer .actions {
            flex-wrap: nowrap;
            flex-shrink: 0;
        }
    }
</style>
